import React from 'react'
import clsx from 'clsx'
import {makeStyles} from 'external/material'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import TableBody from '@mui/material/TableBody'
import moment from 'helpers/moment'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox';
import HomeworkFeedback from './homework-feedback'
import HomeworkFiles from '../homework-files'
import HomeworkComments from './homework-comments'
import { isAfter } from 'external/date-fns'

const useStyles = makeStyles(homeworkBodyStyles)
const initialState = {
  studentFiles: [],
  student: {}
}

export default function HomeworkStudentsBody({context, feedback, selectStudentForFeedback, updateFeedback}) {

  const classes = useStyles()
  const {translate,  homeworkContext, groups} = context  
  const homework = homeworkContext.homeworks.items.find(h=> h.id == context.homeworkId) || {students: []}

  const groupMembers = groups.reduce((acc, {id, students}) => ({...acc, [id]: students}), {})
  const studentRecords = groupMembers[homework.group] || []
  
  const students = homework.students.map(s => {
    const studentRecord = studentRecords.find(r=> r.id == s.student) || {}
    return { ...s, studentRecord }
  }) 

  const {studentHomeworkStatus} = homeworkContext

  function formatDate (date) {
    return moment(date).format(context.languageId == 'es' ? 'DD/MM/YYYY' : 'MM/DD/YYYY')
  }

  if(students.length == 0) return (<></>)

  return (
    <>      
        <TableBody>
        {students.map((s) =>   {   
            const status = studentHomeworkStatus.items.find(studentStatus=> studentStatus.id == s.id)                     
            const lastStatus = status!=undefined ? status.status[status.status.length - 1]: {}

            const expiredHomework = isAfter(new Date(), homework.endDate)

            const statusName = context.homeworkStatus[lastStatus.status]            
          
            const homeworkStatus = expiredHomework && statusName != 'completed' ? 'expired' : statusName


            const statusDate = ['pending','completed'].indexOf(statusName) > -1
            ? formatDate(lastStatus.created)
            :''
            const studentFeedback = feedback.find(f=> f.id == s.id)
            const studentSelected = studentFeedback!=undefined
            const hasScore = lastStatus.score != undefined
            const isLate = moment(lastStatus.created).diff(homework.endDate, 'hours') > 0

            return (          
            <TableRow key={s.id} className={classes.tableRow}>
                <TableCell align="center">
                  {status != undefined && (
                  <Checkbox
                    onChange={e=> selectStudentForFeedback(s.id, !studentSelected, (studentSelected && hasScore)?lastStatus.score:{}) }
                    checked={studentSelected}
                   />)}
                </TableCell>
                <TableCell align="left">{s.studentRecord.name}</TableCell>
                <TableCell align="left"><span className={clsx(classes.statusName, `${homeworkStatus}`)}>{translate('homework-status-' + homeworkStatus)}</span></TableCell>                
                <TableCell align="left"><span className={clsx(classes.statusDate, {'late': isLate})}>{statusDate}</span></TableCell>          
                <TableCell align="center">
                  <Box className={classes.filesContainer}>
                    <HomeworkFiles context={context} passport={s.studentRecord.studentId} files={s.properties.files}  />
                  </Box>
                </TableCell>                          
                <TableCell align="left">
                  <Box>
                    {studentSelected &&
                      <HomeworkFeedback {...{context, homework, studentFeedback, updateFeedback, homeworkStatus}} />
                    }
                    {!studentSelected && hasScore &&
                      <Box>
                        {lastStatus.score} / {homework.properties.score}
                      </Box>
                    }
                    {!studentSelected && !hasScore &&
                      <Box>
                        - / {homework.properties.score}
                      </Box>
                    }
                  </Box>
                </TableCell>
                <TableCell className={classes.actions} style={{top:92}} align="center">                    
                    <HomeworkComments context={context} status={status} score={homework.properties.score} />
                </TableCell>
                <TableCell align="left"></TableCell>
            </TableRow>
            )
        })}
        </TableBody>        
    </>
  )
}

function homeworkBodyStyles() {
    return {       
        statusIndicator: {
            '&.available': {
                backgroundColor: 'orange',
                borderRadius: '100%',
                width: 15,
                height: 15,
                display: 'inline-block',
                marginRight: 10
            }
        },
        title: {          
          cursor: 'pointer',
          width: '30%',
          '&:hover': {
            color: '#127e7e',
            textDecoration: 'underline'
          }
          
        },
        tableRow: {
          '& > td': {
            padding: 6
          }
        },
        statusName: {
          padding: '5px',
          color: '#fff',
          borderRadius: '20px',
          display: 'block',
          width: 100,
          margin: 'auto',
          textAlign: 'center',
          '&.assigned': {
            display: 'none',
            backgroundColor: '#f78d43'
          },
          '&.completed': {
            backgroundColor: '#06b71c'
          },
          '&.pending': {
            backgroundColor: '#0ac7ff'
          },
          '&.expired': {
            backgroundColor: '#afafaf'
          },
          '&.returned': {
            backgroundColor: '#ff0a0a'
          },
          '&.undefined': {
            display: 'none'
          }
          
        },
        statusDate: {
          '&.late': {
            color: 'red'
          }
        },
        scoreField: {
          fontSize: '14pt',
          fontWeight: 'bold'
        },
        scoreTextBox: {
          width: 70,
          height: 30,
          border: '1px solid #ccc',
          marginRight: 15,
          textAlign: 'center',
          padding: '0 5px',
          borderRadious: 4
        },
        filesContainer: {
          marginRight: 20
        },
        visuallyHidden: {
          border: 0,
          clip: 'rect(0 0 0 0)',
          height: 1,
          margin: -1,
          overflow: 'hidden',
          padding: 0,
          position: 'absolute',
          top: 20,
          width: 1,
        },
        personIcon: {
          color: '#128080',
          position: 'relative',
          top: 6
        },
        link: {
          color: '#2c75bd',
          fontSize: '16pt',
          marginRight: 20,
          cursor: 'pointer'
        },        
        feedbackBox: {
          display: 'block',
          margin: '10px 0',
          backgroundColor: '#e8e8e8',
          padding: 5,
          borderRadius: 3,
          '& label': {
            fontSize: '12px'
          }
        },
        feedbackText: {
          fontSize: '12px',
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          padding: 10,
          borderRadius: 4,
          border: '1px solid #ccc',
          width: '100%'
        }
      };
}
